import { convertUTCDateInTimeZone } from "@taabl/common-lib/calculators/date";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Form,
  FormControl,
  InputGroup,
  Row,
  ToggleButton,
  ToggleButtonGroup,
} from "react-bootstrap";
import Datetime from "react-datetime";
import { toast } from "react-toastify";
import LoaderButton from "../components/LoaderButton";
import PageLoader from "../components/PageLoader";
import { Constants } from "../libs/Constants";
import { getList, update } from "../libs/dbFunctions";
import { validateForms } from "../libs/hooksLib";
import { getConfig } from "../Objects/Object";
import "./PlanDetails.scss";

const grids = [
  {
    title: "General Information",
    columns: [
      "taxQualified",
      "employeeDiscount",
      "offeringPeriod",
      "purchasePeriod",
      "LookbackAllowed",
      "resetAllowed",
      "increasesAllowed",
      "decreasesAllowed",
      "fractionalShares",
      "fractionalDigits",
    ],
  },
  {
    title: "Contribution Requirements",
    columns: [
      "minStockAmount",
      "maxStockAmount",
      "minDollarAmount",
      "maxDollarAmount",
      "minPercentAmount",
      "maxPercentAmount",
      "enrollmentStarts",
      "enrollmentEnds",
    ],
  },
];

export default function PlanDetails(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState({});
  const [minStockNoLimit, setMinStockNoLimit] = useState(false);
  const [maxStockNoLimit, setMaxStockNoLimit] = useState(false);
  const [minDollarNoLimit, setMinDollarNoLimit] = useState(false);

  const [planDetails, setPlanDetails] = useState({
    taxQualified: "Yes",
    employeeDiscount: 0,
    purchasePeriod: "3",
    lookBack: "Yes",
    increasesAllowed: 0,
    decreasesAllowed: 0,
    fractionalShares: "No",
    resetAllowed: "Yes",
    offeringPeriod: "3",
    fractionalDigits: "",
    enrollmentStarts: "",
    enrollmentEnds: "",
    offerStartdate: "",
  });
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    setConfig(getConfig(Constants.dbTypes.PLAN_DETAILS));
    onLoad();
  }, [props.isAuthenticated, props.userObj, props.activeCompany]);

  async function onLoad() {
    try {
      const planDetails = await getList(
        Constants.dbTypes.PLAN_DETAILS,
        props.activeCompany
      );
      if (planDetails && planDetails.length > 0) {
        loadPlan(planDetails[0]);
      }
      const periods = await getList(
        Constants.dbTypes.OFFERING_PERIOD,
        props.activeCompany
      );
      if (periods && periods.length > 0) {
        let plan = {
          ...planDetails[0],
          ["offerStartdate"]: periods[0].offerStartdate,
        };
        setPlanDetails(plan);
      }
    } catch (e) {
      toast.error(e);
    }
    setIsLoading(false);
  }
  function loadPlan(plan) {
    let config = getConfig(Constants.dbTypes.PLAN_DETAILS);
    let planLoaded = Object.assign(plan, {
      taxQualified: plan.taxQualified ? "Yes" : "No",
      resetAllowed: plan.resetAllowed ? "Yes" : "No",
      fractionalShares: plan.fractionalShares ? "Yes" : "No",
      LookbackAllowed: plan.lookBack !== "0" ? "Yes" : "No",
      offeringPeriod: plan.lookBack,
    });
    if ("maxContribution" in plan && plan.maxContribution) {
      planLoaded.maxStockAmount = plan.maxContribution["stock"] || "";
      planLoaded.maxPercentAmount = plan.maxContribution["percent"] || "";
      planLoaded.maxDollarAmount = plan.maxContribution["dollar"] || 0;
    }
    if ("minContribution" in plan && plan.minContribution) {
      planLoaded.minStockAmount = plan.minContribution["stock"] || "";
      planLoaded.minPercentAmount = plan.minContribution["percent"] || "";
      planLoaded.minDollarAmount = plan.minContribution["dollar"] || 0;
    }
    if ("fractionalDigits" in plan && plan.fractionalDigits) {
      let digits = config.fieldTypes.fractionalDigits.options;
      let detail = digits.find((item) => item.key === plan.fractionalDigits);
      planLoaded.fractionalDigits = detail.value;
    } else {
      planLoaded.fractionalDigits = "";
    }
    if (!plan.minStockAmount) setMinStockNoLimit(true);
    if (!plan.maxStockAmount) setMaxStockNoLimit(true);
    if ("minDollarAmount" in plan && plan.minDollarAmount === 0)
      setMinDollarNoLimit(true);
    setPlanDetails(plan);
  }
  function handleChange(e) {
    let o = {};
    o = {
      ...planDetails,
      [e.target.id]:
        e.target.type === "number" && e.target.value
          ? parseFloat(e.target.value)
          : e.target.value,
      ["LookbackAllowed"]: planDetails.lookBack !== "0" ? "Yes" : "No",
    };
    if (e.target.id === "minStockAmount") {
      if (o.minStockAmount === null) setMinStockNoLimit(true);
      else setMinStockNoLimit(false);
    }
    if (e.target.id === "maxStockAmount") {
      if (o.maxStockAmount === null) setMaxStockNoLimit(true);
      else setMaxStockNoLimit(false);
    }
    if (e.target.id === "minDollarAmount") {
      if (o.minDollarAmount === 0) setMinDollarNoLimit(true);
      else setMinDollarNoLimit(false);
    }
    setPlanDetails(o);
  }

  async function handleChangeDate(fieldName, selectedDate) {
    let validDate = null;
    if (fieldName === "offerStartdate") {
      validDate = moment(selectedDate, Constants.DATEFORMAT.MMDDYYYYHH)
        .tz(props.activeCompany.timezone)
        .valueOf();
    } else {
      validDate = selectedDate.utc().format(Constants.DATEFORMAT.MMDDYYYY);
    }
    let o = { ...planDetails, [fieldName]: validDate };

    o = { ...o, timeZone: props.activeCompany.timezone };

    setPlanDetails(o);
    setErrors(validateForms(o, config.validationSchema, config.fieldTypes));
  }

  function handleCheckBox(e) {
    let flag;
    let o = { ...planDetails };
    if (e.target.id === "maxStockAmount") {
      flag = !maxStockNoLimit;
      if (flag) o.maxStockAmount = "";
      setMaxStockNoLimit(flag);
    } else if (e.target.id === "minStockAmount") {
      flag = !minStockNoLimit;
      if (flag) o.minStockAmount = "";
      setMinStockNoLimit(flag);
    } else if (e.target.id === "minDollarAmount") {
      flag = !minDollarNoLimit;
      if (flag) o.minDollarAmount = 0;
      o.minDollarNoLimit = flag;
      setMinDollarNoLimit(flag);
    }
    setPlanDetails(o);
  }
  function buildFormControl(field, key) {
    if (field.type === "button") {
      let options = field.options;
      if (
        key !== "fractionalDigits" ||
        (key === "fractionalDigits" && planDetails.fractionalShares !== "No")
      ) {
        return (
          <div className="w-100 espp-wrapper ">
            <ToggleButtonGroup
              type="radio"
              name={key}
              defaultValue={"Yes"}
              value={planDetails[key]}
              onChange={(e) => {
                let o = {
                  ...planDetails,
                  [key]: e,
                };
                if (key !== "LookbackAllowed") {
                  o["LookbackAllowed"] =
                    planDetails.lookBack !== "0" ? "Yes" : "No";
                }
                if (key === "fractionalShares" && o[key] === "Yes")
                  config.fieldTypes["fractionalDigits"].required = true;
                else config.fieldTypes["fractionalDigits"].required = false;
                setPlanDetails(o);
              }}
            >
              {options.map((f) => {
                return (
                  <ToggleButton
                    className="me-2 mt-3 espp-btn"
                    variant="secondary"
                    value={f.value}
                    key={f.key}
                  >
                    {f.value}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            {key === "fractionalDigits" &&
              planDetails.fractionalShares === "Yes" &&
              errors &&
              errors[key] && (
                <span className="error-message">
                  {" "}
                  Please select one of the options to set the Fractional Digits
                  allowed
                </span>
              )}
          </div>
        );
      }
    } else if (field.type === "dropdown") {
      let options = field.options;
      // Check if only for some specific company
      options = options.filter(
        (o) =>
          !o.companyId ||
          (o.companyId && o.companyId === props.activeCompany.id)
      );
      return (
        <React.Fragment>
          <Form.Control
            as="select"
            size="lg"
            className="form-select"
            value={planDetails[key]}
            onChange={handleChange}
          >
            {options.map((f) => {
              return (
                <option key={f.key} value={f.key} type={field.type}>
                  {f.value}
                </option>
              );
            })}
          </Form.Control>
          {errors && errors[key] && (
            <span className="error-message">{errors[key]}</span>
          )}
        </React.Fragment>
      );
    } else if (field.type === "date") {
      let date = moment(planDetails[key]);
      if (field.dateformat) {
        date = moment(planDetails[key], field.dateformat);
      }

      let timezone = props.activeCompany?.timezone
        ? props.activeCompany?.timezone
        : moment.tz();
      if (props.activeCompany?.timezone) {
        date = convertUTCDateInTimeZone(planDetails[key], timezone);
      }
      if (key === "offerStartdate") {
        if (planDetails.offerStartdate) {
          return (
            <>
              <div className="dateControl ">
                <div className="input-group">
                  <FormControl
                    disabled={true}
                    value={date.format(Constants.DATEFORMAT.MMDDYYYYHH)}
                  />
                  <div className="noLimit-spc" />
                </div>
                <p className="font-secondary fw-bold">
                  NOTE: Period will be created in{" "}
                  {props.activeCompany?.timezone} timezone
                </p>
              </div>
            </>
          );
        } else if (!planDetails.offerStartdate) {
          return (
            <div className="dateControl">
              <Datetime
                locale={timezone}
                displayTimeZone={timezone}
                timeFormat={true}
                value={planDetails && planDetails[key] ? date : ""}
                inputProps={{
                  placeholder: field.fieldName,
                  disabled: false,
                  readOnly: true,
                }}
                onChange={(selectedDate) => handleChangeDate(key, selectedDate)}
              />
              <p>
                NOTE: Period will be created in {props.activeCompany?.timezone}{" "}
                timezone
              </p>
            </div>
          );
        }
      } else {
        return (
          <div className="">
            <Datetime
              locale={timezone}
              displayTimeZone={timezone}
              timeFormat={true}
              value={planDetails && planDetails[key] ? date : ""}
              inputProps={{
                placeholder: field.fieldName,
                disabled: false,
                readOnly: true,
              }}
              onChange={(selectedDate) => handleChangeDate(key, selectedDate)}
            />
          </div>
        );
      }
    } else {
      if (field.append) {
        return (
          <InputGroup className="">
            <FormControl
              size="lg"
              value={planDetails[key]}
              type={field.type}
              placeholder={field.fieldName}
              isInvalid={errors && errors[key]}
              onChange={handleChange}
            />
            <InputGroup.Text id="basic-addon2">{field.append}</InputGroup.Text>
            {field.checkbox ? (
              <Form.Check
                className="noLimit-spc tbl-checkbox ps-4 ms-2"
                type="checkbox"
                label={field.checkbox}
                key={key}
                id={key}
              >
                <Form.Check.Input
                  type="checkbox"
                  onChange={(e) => handleCheckBox(e)}
                  checked={minDollarNoLimit}
                />
                <Form.Check.Label>{field.checkbox}</Form.Check.Label>
              </Form.Check>
            ) : (
              <div className="noLimit-spc"></div>
            )}
            <Form.Control.Feedback type="invalid">
              <span>{errors && errors[key]}</span>
            </Form.Control.Feedback>
          </InputGroup>
        );
      } else {
        return (
          <React.Fragment>
            <InputGroup className="is-invalid ">
              <FormControl
                size="lg"
                value={planDetails[key]}
                type={field.type}
                className="quantity"
                placeholder={field.fieldName}
                isInvalid={errors && errors[key]}
                onChange={handleChange}
              />

              {field.checkbox ? (
                <Form.Check
                  className="noLimit-spc tbl-checkbox ps-4"
                  type="checkbox"
                  id={key}
                >
                  <Form.Check.Input
                    type="checkbox"
                    onChange={(e) => handleCheckBox(e)}
                    checked={
                      key.startsWith("min") ? minStockNoLimit : maxStockNoLimit
                    }
                  />
                  <Form.Check.Label>{field.checkbox}</Form.Check.Label>
                </Form.Check>
              ) : (
                <div className="noLimit-spc"></div>
              )}
            </InputGroup>
            <Form.Control.Feedback type="invalid">
              <span>{errors && errors[key]}</span>
            </Form.Control.Feedback>
          </React.Fragment>
        );
      }
    }
  }

  function buildPlaceholder(field) {
    if (field.required) {
      return (
        <div className="label">
          {field.fieldName}
          <span className="text-danger"> * </span>
        </div>
      );
    }
    if (
      field.fieldName !== "Fractional Digits Allowed" ||
      (field.fieldName === "Fractional Digits Allowed" &&
        planDetails.fractionalShares !== "No")
    )
      return <span className="label">{field.fieldName}</span>;
  }
  function buildForm() {
    let config = getConfig(Constants.dbTypes.PLAN_DETAILS);
    if (props.activeCompany.managed) {
      if (grids[1].columns.includes("offerStartdate")) {
        grids[1].columns = grids[1].columns.filter(
          (val) => val !== "offerStartdate"
        );
      }
    } else {
      if (!grids[1].columns.includes("offerStartdate")) {
        grids[1].columns = [...grids[1].columns, "offerStartdate"];
      }
    }
    return (
      <div className="row gx-0">
        {grids.map((grid, index) => {
          return (
            <React.Fragment key={index}>
              <div className="col-md-5 col-xs-12">
                <h3 className="mb-3 mt-4 espp-heading">{grid.title}</h3>
                {grid.columns.map((key) => {
                  const field = config.fieldTypes[key];
                  return (
                    <Form.Group
                      as={Row}
                      controlId={key}
                      key={key}
                      className={`form-group gx-0`}
                    >
                      <Form.Label>{buildPlaceholder(field)}</Form.Label>
                      {buildFormControl(field, key)}
                    </Form.Group>
                  );
                })}
              </div>
              {index === 0 && <div className="col-md-1" />}
              {index < grids.length - 1 && <div className="col-md-1 vl" />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    console.log(planDetails);
    let err = validateForms(
      planDetails,
      config.validationSchema,
      config.fieldTypes
    );

    if (planDetails.offeringPeriod && planDetails.purchasePeriod) {
      let purchasePeriod = parseInt(planDetails.purchasePeriod);
      let offeringPeriod = parseInt(planDetails.offeringPeriod);
      if (purchasePeriod > offeringPeriod) {
        err["purchasePeriod"] =
          "Purchase Period cannot be greater than offering period";
      } else if (offeringPeriod % purchasePeriod !== 0) {
        err["purchasePeriod"] =
          "Purchase Period should be divisble with offering period";
      }
    }

    setErrors(err);
    if (Object.keys(err).length === 0) {
      setIsLoading(true);
      if (props.activeCompany.id === Constants.TaablAdminCompany) {
        toast.error("Not allowed to create ESPP for TaablAdmin Company");
        setIsLoading(false);
      } else {
        try {
          if (planDetails) {
            let plan = parseToPlanDetails({ ...planDetails });
            plan.companyId = props.activeCompany.id;
            await update(Constants.dbTypes.PLAN_DETAILS, plan);
          }
          props.history.push("/");
        } catch (e) {
          if (e.response && e.response.data && e.response.data.error) {
            toast.error(e.response.data.error);
          } else {
            toast.error(e);
          }
          setIsLoading(false);
        }
      }
    }
  }
  function parseToPlanDetails(planDetails) {
    let planDetailsValue = Object.assign(planDetails, {
      taxQualified: planDetails.taxQualified === "Yes" ? true : false,
      resetAllowed: planDetails.resetAllowed === "Yes" ? true : false,
      fractionalShares: planDetails.fractionalShares === "Yes" ? true : false,
      lookBack: planDetails.LookbackAllowed ? planDetails.offeringPeriod : "0",
      maxDollarAmount: planDetails.maxDollarAmount,
      minDollarAmount: !minDollarNoLimit ? planDetails.minDollarAmount : 0,
    });
    if (
      "fractionalShares" in planDetailsValue &&
      planDetailsValue.fractionalShares
    ) {
      planDetailsValue.fractionalDigits = Number(
        planDetails.fractionalDigits.charAt(0)
      );
    } else {
      planDetailsValue.fractionalDigits = undefined;
    }
    if (planDetails.maxStockAmount) {
      planDetailsValue.maxStockAmount = planDetails.maxStockAmount;
    }
    if (planDetails.minStockAmount) {
      planDetailsValue.minStockAmount = planDetails.minStockAmount;
    }
    if (planDetails.maxPercentAmount) {
      planDetailsValue.maxPercentAmount = planDetails.maxPercentAmount;
    }
    if (planDetails.minPercentAmount) {
      planDetailsValue.minPercentAmount = planDetails.minPercentAmount;
    }
    return planDetailsValue;
  }
  return (
    <PageLoader isActive={isLoading} individual={false}>
      <div className="container NewNote p5">
        <Row>
          <Col xs={12} sm={12} lg={12} md={12} xl={12}>
            <Card>
              <Card.Body>
                <Card.Title as="h2" className="row gx-0">
                  ESPP Details
                </Card.Title>
                <div>
                  <Form
                    noValidate
                    validated={!(errors && Object.keys(errors).length > 0)}
                  >
                    {buildForm()}
                  </Form>
                  <LoaderButton
                    size="lg"
                    className="me-2"
                    type="submit"
                    id="action"
                    variant="primary"
                    isLoading={isLoading}
                    onClick={handleSubmit}
                  >
                    Save
                  </LoaderButton>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </PageLoader>
  );
}
